import { useLanguageAwareRouting } from '@gofranz/common-components';
import type { Message, SingleMessageResponse } from '@gofranz/formshive-common';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Container,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCapture,
  IconDownload,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { NostrDecryptButton } from '../../../components/Message/NostrDecryptButton';
import { useRustyState } from '../../../state';

export function AccountMessageDetailPage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const [messageData, setMessageData] = useState<SingleMessageResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [decryptedData, setDecryptedData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    const fetchMessage = async () => {
      if (!messageId) {
        setError('Message ID not provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await api.getMessage(messageId);
        setMessageData(response);
      } catch (err: any) {
        console.error('Failed to fetch message:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load message');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessage();
  }, [messageId]);

  const handleBack = () => {
    navigate(createLanguageURL('/account/messages'));
  };

  const handleToggleSpam = async () => {
    if (!messageData) return;

    setIsBusy(true);
    try {
      const customNotification = {
        onSuccess: {
          title: t('messageTable.messageUpdated'),
          message: messageData.message.is_spam
            ? t('messageTable.markedAsHam')
            : t('messageTable.markedAsSpam'),
        },
      };

      await api.updateMessage(
        messageData.message.id,
        { user_marked_spam: !messageData.message.is_spam },
        customNotification
      );

      setMessageData({
        ...messageData,
        message: {
          ...messageData.message,
          is_spam: !messageData.message.is_spam,
        },
      });
    } catch (err) {
      console.error('Failed to toggle spam status:', err);
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async () => {
    if (
      !messageData ||
      !window.confirm(
        t('messageTable.confirmDelete') || 'Are you sure you want to delete this message?'
      )
    ) {
      return;
    }

    setIsBusy(true);
    try {
      await api.deleteMessage(messageData.message.id);
      navigate(createLanguageURL('/account/messages'));
    } catch (err) {
      console.error('Failed to delete message:', err);
    } finally {
      setIsBusy(false);
    }
  };

  const downloadFile = async (fileId: string) => {
    try {
      const signedUrl = await useRustyState.getState().downloadFile(fileId);
      if (signedUrl) {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.setAttribute('download', 'true');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  const downloadMessage = (format: 'json' | 'txt') => {
    if (!messageData) return;

    const data =
      messageData.message.encryption_mode === 'nostr'
        ? decryptedData
        : JSON.parse(messageData.message.data || '{}');

    if (!data) return;

    let content = '';
    if (format === 'json') {
      content = JSON.stringify(data, null, 2);
    } else {
      Object.keys(data).forEach((key) => {
        content += `${key}: ${data[key]}\n`;
      });
    }

    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `formshive-message-${messageData.message.id}.${format}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const formatTimeDifference = (viewTime: string, messageTime: string): string => {
    const viewDate = new Date(viewTime);
    const messageDate = new Date(messageTime);
    const diffMs = messageDate.getTime() - viewDate.getTime();

    if (diffMs < 0) {
      return 'Message submitted before view';
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} after view`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} after view`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} after view`;
    } else {
      return `${diffSeconds} second${diffSeconds > 1 ? 's' : ''} after view`;
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Container fluid>
        <Group mb="md">
          <Skeleton height={36} width={120} />
          <Skeleton height={32} width={200} />
        </Group>
        <Stack gap="md">
          <Card withBorder>
            <Skeleton height={20} width="60%" mb="md" />
            <Skeleton height={100} width="100%" />
          </Card>
          <SimpleGrid cols={3}>
            <Card withBorder>
              <Skeleton height={14} width="50%" mb="xs" />
              <Skeleton height={24} width="70%" />
            </Card>
            <Card withBorder>
              <Skeleton height={14} width="50%" mb="xs" />
              <Skeleton height={24} width="70%" />
            </Card>
            <Card withBorder>
              <Skeleton height={14} width="50%" mb="xs" />
              <Skeleton height={24} width="70%" />
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>
    );
  }

  // Error state
  if (error || !messageData) {
    return (
      <Container fluid>
        <Group mb="md">
          <Button variant="light" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
            {t('common.back') || 'Back'}
          </Button>
        </Group>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('common.error') || 'Error'}
          color="red"
        >
          {error || 'Message not found'}
        </Alert>
      </Container>
    );
  }

  const { message, form, files, view } = messageData;
  const isNostrEncrypted = message.encryption_mode === 'nostr';
  const messageContent = isNostrEncrypted
    ? decryptedData
    : message.data
      ? JSON.parse(message.data)
      : null;

  return (
    <Container fluid>
      {/* Header with back button */}
      <Group mb="lg" justify="space-between">
        <Group>
          <ActionIcon variant="light" size="lg" onClick={handleBack}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Title order={2}>{t('messageDetail.title') || 'Message Details'}</Title>
        </Group>
        <Group>
          <Button
            onClick={handleToggleSpam}
            loading={isBusy}
            color={message.is_spam ? 'teal' : 'red'}
            size="sm"
            leftSection={<IconCapture size={16} />}
          >
            {message.is_spam ? t('messageTable.markAsHam') : t('messageTable.markAsSpam')}
          </Button>
          <Button
            onClick={handleDelete}
            loading={isBusy}
            color="red"
            variant="outline"
            size="sm"
            leftSection={<IconTrash size={16} />}
          >
            {t('messageTable.delete')}
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        {/* Message metadata */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Card padding="md" withBorder>
            <Text size="sm" c="dimmed">
              {t('messageTable.form') || 'Form'}
            </Text>
            <Text size="lg" fw={600}>
              {form?.title || 'Unknown Form'}
            </Text>
          </Card>

          <Card padding="md" withBorder>
            <Text size="sm" c="dimmed">
              {t('messageTable.createdAt') || 'Created At'}
            </Text>
            <Tooltip label={new Date(message.created_at).toLocaleString()}>
              <Text size="lg" fw={600}>
                {new Date(message.created_at).toLocaleDateString()}
              </Text>
            </Tooltip>
          </Card>

          <Card padding="md" withBorder>
            <Text size="sm" c="dimmed">
              {t('messageTable.spam') || 'Spam Status'}
            </Text>
            <Badge color={message.is_spam ? 'red' : 'green'} size="lg">
              {message.is_spam ? 'Spam' : 'Ham'} ({message.spam_score.toFixed(2)})
            </Badge>
          </Card>

          <Card padding="md" withBorder>
            <Text size="sm" c="dimmed">
              {t('messageDetail.encryption') || 'Encryption'}
            </Text>
            <Badge
              color={
                message.encryption_mode === 'nostr'
                  ? 'green'
                  : message.encryption_mode === 'server'
                    ? 'blue'
                    : 'gray'
              }
              size="lg"
            >
              {message.encryption_mode === 'nostr'
                ? 'E2E Encrypted'
                : message.encryption_mode === 'server'
                  ? 'Server Encrypted'
                  : 'None'}
            </Badge>
          </Card>
        </SimpleGrid>

        {/* Message content */}
        <Card padding="lg" withBorder>
          <Title order={4} mb="md">
            {t('messageTable.content') || 'Content'}
          </Title>

          {isNostrEncrypted ? (
            <NostrDecryptButton
              message={
                {
                  ...message,
                  data: message.data || message.data_encrypted || '',
                } as Message
              }
              onDecrypted={setDecryptedData}
            />
          ) : messageContent ? (
            <Box>
              <Code block>
                {Object.keys(messageContent).map((key) => (
                  <Box key={`content-${key}`}>
                    <Text>
                      {key}:{' '}
                      {Array.isArray(messageContent[key])
                        ? messageContent[key].join(', ')
                        : String(messageContent[key])}
                    </Text>
                  </Box>
                ))}
              </Code>
              <Group mt="md">
                <Button
                  onClick={() => downloadMessage('json')}
                  color="blue"
                  size="sm"
                  leftSection={<IconDownload size={16} />}
                >
                  {t('messageTable.downloadJson')}
                </Button>
                <Button
                  onClick={() => downloadMessage('txt')}
                  color="blue"
                  variant="light"
                  size="sm"
                  leftSection={<IconDownload size={16} />}
                >
                  {t('messageTable.downloadTxt')}
                </Button>
              </Group>
            </Box>
          ) : (
            <Text c="dimmed">No content available</Text>
          )}
        </Card>

        {/* Files */}
        {files && files.length > 0 && (
          <Card padding="lg" withBorder>
            <Title order={4} mb="md">
              {t('messageTable.files') || 'Files'}
            </Title>
            <Stack gap="sm">
              {files.map((file) => (
                <Group key={file.id} justify="space-between">
                  <Text>
                    {file.filename} ({file.size} bytes)
                  </Text>
                  <Button
                    onClick={() => downloadFile(file.id)}
                    color="gray"
                    size="compact-sm"
                    leftSection={<IconDownload size={14} />}
                  >
                    {t('common.download') || 'Download'}
                  </Button>
                </Group>
              ))}
            </Stack>
          </Card>
        )}

        {/* View Analytics */}
        <Card padding="lg" withBorder>
          <Title order={4} mb="md">
            {t('messageDetail.viewAnalytics')}
          </Title>

          {view ? (
            <Box>
              <Group gap="md" mb="xs">
                <Badge color="blue" variant="light">
                  {view.device_type || 'Unknown'} Device
                </Badge>
                <Badge color="green" variant="light">
                  {view.browser_name || 'Unknown'} Browser
                </Badge>
                {view.location?.country_code && (
                  <Badge color="orange" variant="light">
                    {view.location.country_code}
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                Form submitted{' '}
                <Text span fw={500}>
                  {formatTimeDifference(view.created_at, message.created_at)}
                </Text>
              </Text>
              {view.traffic_source && (
                <Text size="xs" c="dimmed">
                  Traffic source:{' '}
                  <Text span fw={500}>
                    {view.traffic_source}
                  </Text>
                </Text>
              )}
              <Box mb="sm" mt="sm">
                <Text size="xs" fw={500} c="orange">
                  {t('messageDetail.location')}:
                </Text>
                {(() => {
                  const loc = message.location;
                  if (!loc) {
                    return (
                      <Text size="xs" c="dimmed">
                        Unknown
                      </Text>
                    );
                  }
                  const locationParts = [
                    loc.city,
                    loc.region_name,
                    loc.country_name || loc.country_code,
                  ].filter(Boolean);
                  return (
                    <Box>
                      <Text size="xs" c="dimmed">
                        Location:{' '}
                        <Text span fw={500}>
                          {locationParts.join(', ') || 'Unknown'}
                        </Text>
                      </Text>
                      {loc.timezone && (
                        <Text size="xs" c="dimmed">
                          Timezone:{' '}
                          <Text span fw={500}>
                            {loc.timezone}
                          </Text>
                        </Text>
                      )}
                      {loc.latitude && loc.longitude && (
                        <Text size="xs" c="dimmed">
                          Coordinates:{' '}
                          <Text span fw={500}>
                            {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}
                          </Text>
                        </Text>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          ) : (
            <Text size="sm" c="dimmed">
              {t('messageDetail.noViewData')}
            </Text>
          )}
        </Card>
      </Stack>
    </Container>
  );
}
